import React from 'react';
import { Transaction } from '../../types';
import { Button } from '../ui/button';
import { PROVIDERS } from '../../lib/constants';

interface TransactionReceiptProps {
  transaction: Transaction;
  onClose: () => void;
  onPrint: () => void;
  onReverse?: (transactionId: string) => void;
}

const TransactionReceipt: React.FC<TransactionReceiptProps> = ({ 
  transaction, 
  onClose,
  onPrint,
  onReverse
}) => {
  // Helper para determinar el tipo específico de detalles
  const getTransactionDetails = () => {
    switch (transaction.type) {
      case 'RECARGA':
        return transaction.details as any;
      case 'PAQUETE':
        return transaction.details as any;
      case 'FACTURA':
        return transaction.details as any;
      case 'SEGURO':
        return transaction.details as any;
      default:
        return {};
    }
  };

  const details = getTransactionDetails();
  
  // Busca el nombre del proveedor desde constants.ts (fuente única de verdad)
  const getProviderNameById = (providerId: string): string => {
    for (const type in PROVIDERS) {
      const list = PROVIDERS[type as keyof typeof PROVIDERS] as { id: string; name: string }[];
      const provider = list.find(p => p.id === providerId);
      if (provider) return provider.name;
    }
    return providerId; // Si no se encuentra, devolver el ID original
  };
  const getStatusColor = () => {
    switch (transaction.status) {
      case 'COMPLETED':
        return 'text-green-500';
      case 'PENDING':
        return 'text-yellow-500';
      case 'FAILED':
        return 'text-red-500';
      case 'REVERSED':
        return 'text-blue-500';
      default:
        return 'text-gray-500';
    }
  };

  // Calcular si la transacción todavía puede ser reversada (10 minutos desde su creación)
  const canBeReversed = () => {
    if (transaction.type !== 'RECARGA' || transaction.status !== 'COMPLETED' || !transaction.reversible) {
      return false;
    }

    const transactionTime = new Date(transaction.date).getTime();
    const now = new Date().getTime();
    const timeDifference = now - transactionTime; // en milisegundos
    const minutesPassed = timeDifference / (1000 * 60);
    
    return minutesPassed <= 10;
  };

  // Calcular el tiempo restante para poder revertir la transacción
  const getRemainingTime = () => {
    if (!canBeReversed()) return 0;
    
    const transactionTime = new Date(transaction.date).getTime();
    const now = new Date().getTime();
    const timeDifference = now - transactionTime; // en milisegundos
    const minutesPassed = timeDifference / (1000 * 60);
    
    return Math.round(10 - minutesPassed);
  };

  const getStatusIcon = () => {
    switch (transaction.status) {
      case 'COMPLETED':
        return (
          <svg className="h-20 w-20 text-green-500" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
          </svg>
        );
      case 'PENDING':
        return (
          <svg className="h-20 w-20 text-yellow-500" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        );
      case 'FAILED':
        return (
          <svg className="h-20 w-20 text-red-500" viewBox="0 0 24 24" fill="currentColor">
            <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const getTransactionTitle = () => {
    switch (transaction.type) {
      case 'RECARGA':
        return 'Recarga Exitosa';
      case 'PAQUETE':
        return 'Compra de Paquete Exitosa';
      case 'FACTURA':
        return 'Pago de Factura Exitoso';
      case 'SEGURO':
        return 'Compra de Seguro Exitosa';
      default:
        return 'Transacción Exitosa';
    }
  };

  const getTransactionSubtitle = () => {
    switch (transaction.type) {
      case 'RECARGA':
        return `Se ha realizado una recarga de RD$ ${transaction.amount} al número ${details.phoneNumber}.`;
      case 'PAQUETE':
        return `Se ha comprado el paquete ${details.packageName} para el número ${details.phoneNumber}.`;
      case 'FACTURA':
        return `Se ha pagado la factura de ${getProviderNameById(transaction.provider)} por RD$ ${transaction.amount}.`;
      case 'SEGURO':
        return `Se ha comprado el seguro ${details.planName} por RD$ ${transaction.amount}.`;
      default:
        return '';
    }
  };

  return (
    <div className="text-center">
      <div className="flex flex-col items-center justify-center mb-6">
        {getStatusIcon()}
        <h2 className="text-2xl font-bold mt-4">{getTransactionTitle()}</h2>
        <p className="text-gray-600 mt-2">{getTransactionSubtitle()}</p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <div className="grid grid-cols-2 gap-4 text-left">
          <div className="col-span-2 border-b pb-2 mb-2">
            <p className="font-bold text-xl">Resumen de Transacción</p>
          </div>
          
          {transaction.type === 'RECARGA' && (
            <>
              <div>
                <p className="text-gray-500">Compañía:</p>
                <p className="font-medium">
                  {details.provider || transaction.provider || 
                   (details.providerId ? getProviderNameById(details.providerId) : 
                   transaction.providerId ? getProviderNameById(transaction.providerId) : 
                   "Desconocido")}
                </p>
              </div>
              <div>
                <p className="text-gray-500">Número:</p>
                <p className="font-medium">{details.phoneNumber}</p>
              </div>
            </>
          )}

          {transaction.type === 'PAQUETE' && (
            <>
              <div>
                <p className="text-gray-500">Compañía:</p>
                <p className="font-medium">{getProviderNameById(transaction.provider)}</p>
              </div>
              <div>
                <p className="text-gray-500">Número:</p>
                <p className="font-medium">{details.phoneNumber}</p>
              </div>
              <div>
                <p className="text-gray-500">Paquete:</p>
                <p className="font-medium">{details.packageName}</p>
              </div>
            </>
          )}

          {transaction.type === 'FACTURA' && (
            <>
              <div>
                <p className="text-gray-500">Servicio:</p>
                <p className="font-medium">{getProviderNameById(transaction.provider)}</p>
              </div>
              <div>
                <p className="text-gray-500">Número de servicio:</p>
                <p className="font-medium">{details.serviceNumber}</p>
              </div>
              {details.customerName && (
                <div>
                  <p className="text-gray-500">Cliente:</p>
                  <p className="font-medium">{details.customerName}</p>
                </div>
              )}
            </>
          )}

          {transaction.type === 'SEGURO' && (
            <>
              <div>
                <p className="text-gray-500">Tipo de seguro:</p>
                <p className="font-medium">{details.insuranceType}</p>
              </div>
              <div>
                <p className="text-gray-500">Plan:</p>
                <p className="font-medium">{details.planName}</p>
              </div>
              <div>
                <p className="text-gray-500">Teléfono:</p>
                <p className="font-medium">{details.customerPhone}</p>
              </div>
            </>
          )}

          <div>
            <p className="text-gray-500">Monto:</p>
            <p className="font-bold">RD$ {transaction.amount}</p>
          </div>
          <div>
            <p className="text-gray-500">Estado:</p>
            <p className={`font-bold ${getStatusColor()}`}>
              {transaction.status === 'COMPLETED' ? 'Completado' : 
               transaction.status === 'PENDING' ? 'Pendiente' : 
               transaction.status === 'REVERSED' ? 'Reversado' : 'Fallido'}
            </p>
          </div>
          <div>
            <p className="text-gray-500">Fecha:</p>
            <p className="font-medium">{new Date(transaction.date).toLocaleString('es-DO')}</p>
          </div>
          <div>
            <p className="text-gray-500">Referencia:</p>
            <p className="font-medium">{transaction.reference}</p>
          </div>
          <div>
            <p className="text-gray-500">ID de Transacción:</p>
            <p className="font-medium">{transaction.xid || transaction.id}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-3">
        {canBeReversed() && onReverse && (
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-700 mb-2">
              <svg className="w-5 h-5 inline-block mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Puede revertir esta recarga durante los próximos {getRemainingTime()} minutos.
            </p>
            <Button 
              variant="danger" 
              size="touch" 
              onClick={() => onReverse && onReverse(transaction.id)}
              fullWidth
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Revertir Recarga
            </Button>
          </div>
        )}
        
        <Button
          variant="primary"
          size="touch"
          onClick={onPrint}
          fullWidth
        >
          {transaction.url ? (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Ver / Descargar Comprobante
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimir Ticket
            </>
          )}
        </Button>
        <Button 
          variant="outline" 
          size="touch" 
          onClick={onClose}
          fullWidth
        >
          Nueva Operación
        </Button>
      </div>
    </div>
  );
};

export default TransactionReceipt;
